/*
 * SNUGLER redesign: scroll-driven loft story.
 * Needs snugler-core.js, which must load first.
 */
(() => {
  const SR = window.SnuglerRedesign;
  if (!SR || !SR.register || SR.modules.has('loft')) return;
  const { isReduced, finePointer, clamp, range, lerp, smooth, easeOut, easeBack, fmt, onScroll, whenVisible, store, selectVariant } = SR.util;

  /* ------------------------------------------------------------------ loft story */

  function initLoft(root) {
    const loft = root.querySelector('[data-snx-loft]');
    if (!loft || loft.__snx) return;
    loft.__snx = true;

    const svg = loft.querySelector('.snx-loft__svg');
    const part = (name) => svg.querySelector(`[data-snx-part="${name}"]`);
    const els = {
      mattress: part('mattress'),
      skirtBand: part('skirt-band'),
      skirtLeft: part('skirt-left'),
      skirtRight: part('skirt-right'),
      fill: part('fill'),
      fillTexture: part('fill-texture'),
      cover: part('cover'),
      stitches: part('stitches'),
      bag: part('bag'),
      bagOutline: part('bag-outline'),
      bagCrinkles: part('bag-crinkles'),
    };
    const captions = Array.from(loft.querySelectorAll('[data-snx-caption]'));
    const progressFill = loft.querySelector('[data-snx-loft-progress]');
    const tags = {};
    const leaders = {};
    const dots = {};
    ['cover', 'fill', 'skirt'].forEach((k) => {
      tags[k] = loft.querySelector(`[data-snx-tag="${k}"]`);
      leaders[k] = svg.querySelector(`[data-snx-leader="${k}"]`);
      dots[k] = svg.querySelector(`[data-snx-dot="${k}"]`);
    });

    const X0 = 80;
    const X1 = 860;
    const MAT_TOP = 420;
    const MAT_BOT = 590;
    const CELLS = 8;
    const CELL_W = (X1 - X0) / CELLS;
    const VIEW_H = 660;
    const LABEL_X = 912;

    // narrow screens hide the side labels, so crop the drawing to fill the width
    const narrow = window.matchMedia('(max-width: 989px)');
    const setViewBox = () => svg.setAttribute('viewBox', narrow.matches ? '44 96 852 548' : '0 0 1200 660');
    setViewBox();
    if (narrow.addEventListener) narrow.addEventListener('change', setViewBox);

    // stitch dots (cell boundaries)
    const stitchNodes = [];
    for (let i = 1; i < CELLS; i++) {
      const c = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      c.setAttribute('r', '4.5');
      els.stitches.appendChild(c);
      stitchNodes.push(c);
    }

    function quiltTop(baseY, domes, scale) {
      // returns path segment across the top from X0 to X1 with domed cells
      let d = '';
      for (let i = 0; i < CELLS; i++) {
        const xa = X0 + i * CELL_W;
        const xb = xa + CELL_W;
        const h = domes[i] * scale;
        d += ` Q ${fmt(xa + CELL_W / 2)} ${fmt(baseY - h * 2)} ${fmt(xb)} ${fmt(baseY)}`;
      }
      return d;
    }

    let lastPhase = -1;
    let exploded = false;

    function draw(p) {
      const bagOpen = smooth(range(p, 0.06, 0.16));
      const loftRaw = range(p, 0.12, 0.42);
      const loftBody = easeBack(loftRaw);
      const explode = smooth(range(p, 0.46, 0.58)) * (1 - smooth(range(p, 0.72, 0.84)));
      const wrap = smooth(range(p, 0.8, 0.95));

      const domes = [];
      for (let i = 0; i < CELLS; i++) {
        const t = clamp((loftRaw - i * 0.045) / (1 - 0.045 * (CELLS - 1)));
        domes.push(t <= 0 ? 0 : easeBack(t));
      }

      // skirt band sits on the mattress
      const skirtY = MAT_TOP - 4;
      els.skirtBand.setAttribute('d', `M ${X0 + 6} ${skirtY} L ${X1 - 6} ${skirtY}`);

      // side drops wrap down the mattress and tuck under
      const dropEnd = lerp(MAT_TOP + 14, MAT_BOT + 6, wrap);
      const tuck = range(wrap, 0.78, 1) * 54;
      const sx = 10;
      let left = `M ${X0 + 8} ${skirtY} Q ${X0 - sx} ${skirtY} ${X0 - sx} ${skirtY + 14} L ${X0 - sx} ${fmt(dropEnd)}`;
      let right = `M ${X1 - 8} ${skirtY} Q ${X1 + sx} ${skirtY} ${X1 + sx} ${skirtY + 14} L ${X1 + sx} ${fmt(dropEnd)}`;
      if (tuck > 0.5) {
        left += ` Q ${X0 - sx} ${fmt(dropEnd + 12)} ${X0 + 6} ${fmt(dropEnd + 12)} L ${fmt(X0 + 6 + tuck)} ${fmt(dropEnd + 12)}`;
        right += ` Q ${X1 + sx} ${fmt(dropEnd + 12)} ${X1 - 6} ${fmt(dropEnd + 12)} L ${fmt(X1 - 6 - tuck)} ${fmt(dropEnd + 12)}`;
      }
      els.skirtLeft.setAttribute('d', left);
      els.skirtRight.setAttribute('d', right);

      // fill body
      const fillLift = explode * 46;
      const fillBottom = MAT_TOP - 9 - fillLift;
      const fillH = lerp(10, 92, loftBody);
      const fillTop = fillBottom - fillH;
      const r = Math.min(22, fillH / 2);
      const fillPath =
        `M ${X0} ${fmt(fillBottom)} L ${X0} ${fmt(fillTop + r)} Q ${X0} ${fmt(fillTop)} ${X0 + r} ${fmt(fillTop)}` +
        ` L ${X0} ${fmt(fillTop)}` +
        quiltTop(fillTop, domes, 7) +
        ` L ${X1} ${fmt(fillTop + r)} L ${X1} ${fmt(fillBottom)} Z`;
      els.fill.setAttribute('d', fillPath);
      els.fillTexture.setAttribute('d', fillPath);
      els.fillTexture.style.opacity = String(0.25 + 0.65 * clamp(loftRaw * 1.4));

      // cover lifts like a lid during the exploded view
      const coverLift = explode * 132;
      const coverTop = fillTop - 7 - coverLift;
      const coverBottom = fillBottom - coverLift - 2;
      const coverPath =
        `M ${X0 - 2} ${fmt(coverBottom)} L ${X0 - 2} ${fmt(coverTop + 10)} Q ${X0 - 2} ${fmt(coverTop)} ${X0 + 8} ${fmt(coverTop)} L ${X0} ${fmt(coverTop)}` +
        quiltTop(coverTop, domes, 13) +
        ` L ${X1 + 2} ${fmt(coverTop + 10)} L ${X1 + 2} ${fmt(coverBottom)}`;
      els.cover.setAttribute('d', coverPath);
      stitchNodes.forEach((node, i) => {
        node.setAttribute('cx', fmt(X0 + (i + 1) * CELL_W));
        node.setAttribute('cy', fmt(coverTop + 1));
      });

      // vacuum bag
      const bagTop = coverTop - 16;
      const bagBottom = MAT_TOP + 4;
      els.bagOutline.setAttribute('d', `M ${X0 - 22} ${fmt(bagTop + 14)} Q ${X0 - 22} ${fmt(bagTop)} ${X0 - 8} ${fmt(bagTop)} L ${X1 + 8} ${fmt(bagTop)} Q ${X1 + 22} ${fmt(bagTop)} ${X1 + 22} ${fmt(bagTop + 14)} L ${X1 + 22} ${bagBottom} L ${X0 - 22} ${bagBottom} Z`);
      let crinkles = '';
      for (let i = 0; i < 14; i++) {
        const x = X0 + 20 + i * 54;
        crinkles += ` M ${x} ${fmt(bagTop + 6)} l 12 ${fmt(bagBottom - bagTop - 12)}`;
      }
      els.bagCrinkles.setAttribute('d', crinkles);
      els.bag.style.opacity = String(1 - bagOpen);
      els.bag.style.transformOrigin = '470px 420px';
      els.bag.style.transform = `scale(${fmt(1 + bagOpen * 0.06)})`;

      // mattress dims during the exploded view
      els.mattress.style.opacity = String(1 - explode * 0.55);

      // labels
      const showLabels = explode > 0.62;
      const positions = {
        cover: { y: coverTop - 4, x: X1 + 8 },
        fill: { y: fillTop + fillH * 0.55, x: X1 + 4 },
        skirt: { y: MAT_TOP + 24, x: X1 + 14 },
      };
      const labelY = { cover: Math.min(positions.cover.y, 150), fill: 300, skirt: 470 };
      const drawIn = clamp((explode - 0.62) / 0.3);
      Object.keys(positions).forEach((key) => {
        const pos = positions[key];
        const ly = labelY[key];
        leaders[key].setAttribute('d', `M ${fmt(pos.x)} ${fmt(pos.y)} C ${fmt(pos.x + 30)} ${fmt(pos.y)} ${LABEL_X - 30} ${ly} ${LABEL_X - 8} ${ly}`);
        leaders[key].style.strokeDashoffset = String(1 - drawIn);
        dots[key].setAttribute('cx', fmt(pos.x));
        dots[key].setAttribute('cy', fmt(pos.y));
        dots[key].style.opacity = String(drawIn);
        if (tags[key]) {
          tags[key].style.top = `${fmt((ly / VIEW_H) * 100)}%`;
          tags[key].classList.toggle('is-on', showLabels);
        }
      });

      if (showLabels !== exploded) {
        exploded = showLabels;
        loft.classList.toggle('is-exploded', showLabels);
      }

      const phase = p < 0.14 ? 0 : p < 0.44 ? 1 : p < 0.78 ? 2 : 3;
      if (phase !== lastPhase) {
        lastPhase = phase;
        captions.forEach((c, i) => c.classList.toggle('is-active', i === phase));
      }
      if (progressFill) progressFill.style.transform = `scaleX(${fmt(p * 1000) / 1000})`;
    }

    if (isReduced()) {
      draw(0.62);
      return;
    }

    const track = loft.querySelector('.snx-loft__track');
    let current = 0;
    let target = 0;
    let animating = false;
    const tick = () => {
      const diff = target - current;
      if (Math.abs(diff) < 0.0008) {
        current = target;
        draw(current);
        animating = false;
        return;
      }
      current += diff * 0.16;
      draw(current);
      requestAnimationFrame(tick);
    };
    onScroll(loft, (vh) => {
      const rect = track.getBoundingClientRect();
      const total = rect.height - vh;
      target = total > 0 ? clamp(-rect.top / total) : 0;
      if (!animating) {
        animating = true;
        requestAnimationFrame(tick);
      }
    });
    draw(0);
  }

  SR.register('loft', (root) => {
    initLoft(root);
  });
})();
